import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Floating horizontal scrollbar for tall overflow-x hosts (code blocks,
 * tables). When the host extends past the bottom of the page scroller, a
 * fixed bar sits just above the scroller's bottom edge so you can pan
 * sideways without scrolling to the end of the block first.
 *
 * Portaled to document.body so sheet `transform` / `overflow:hidden` don't
 * trap `position: fixed`.
 *
 * The track/thumb are custom DOM (not a native OS scrollbar) — on Windows,
 * styling `::-webkit-scrollbar` on an 8px-tall overflow host still falls
 * back to the classic arrow bar. Owning the pill ourselves matches the
 * iOS look reliably.
 */

/** Prefer real page scrollers — nested overflow:auto hosts throw off `bottom`. */
const PAGE_SCROLLERS =
  '.doc-fullpage__scroll, .doc-sheet__body, .doc-area__scroll, .mfv-content__wrapper';

const MIN_THUMB = 36;

/**
 * One scroll listener per page scroller, shared by every code block and table.
 * Each host used to listen on window (capture) and measure itself on every
 * tick — a long doc did that work once per block per frame, which is the
 * wheel stutter. Off-screen hosts are skipped via IntersectionObserver.
 */
const scrollerGroups = new WeakMap();
const hostByEl = new WeakMap();

function portRectOf(scroller) {
  if (scroller === document.documentElement || scroller === document.body) {
    return { top: 0, bottom: window.innerHeight };
  }
  return scroller.getBoundingClientRect();
}

function flushGroup(group) {
  group.raf = 0;
  const portRect = portRectOf(group.scroller);
  group.hosts.forEach((host) => {
    if (!host.visible) return;
    host.measure(portRect);
  });
}

function scheduleGroup(group) {
  if (group.raf) return;
  group.raf = requestAnimationFrame(() => flushGroup(group));
}

function ensureGroup(scroller) {
  let group = scrollerGroups.get(scroller);
  if (group) return group;
  group = { scroller, hosts: new Set(), raf: 0, io: null, onScroll: null, onResize: null };
  const scrollTarget = scroller === document.documentElement ? document : scroller;
  group.onScroll = () => scheduleGroup(group);
  group.onResize = () => scheduleGroup(group);
  scrollTarget.addEventListener('scroll', group.onScroll, { passive: true });
  window.addEventListener('resize', group.onResize);
  if (typeof IntersectionObserver === 'function') {
    group.io = new IntersectionObserver(
      (entries) => {
        let needsMeasure = false;
        entries.forEach((entry) => {
          const host = hostByEl.get(entry.target);
          if (!host || host.group !== group) return;
          if (host.visible === entry.isIntersecting) return;
          host.visible = entry.isIntersecting;
          if (!entry.isIntersecting) host.hide();
          else needsMeasure = true;
        });
        if (needsMeasure) scheduleGroup(group);
      },
      {
        root: scroller === document.documentElement || scroller === document.body ? null : scroller,
        rootMargin: '160px 0px',
      }
    );
  }
  scrollerGroups.set(scroller, group);
  return group;
}

function releaseGroup(group) {
  if (group.hosts.size) return;
  const scrollTarget = group.scroller === document.documentElement ? document : group.scroller;
  scrollTarget.removeEventListener('scroll', group.onScroll);
  window.removeEventListener('resize', group.onResize);
  group.io?.disconnect();
  if (group.raf) cancelAnimationFrame(group.raf);
  scrollerGroups.delete(group.scroller);
}

function findPageScroller(node) {
  const preferred = node?.closest?.(PAGE_SCROLLERS);
  if (preferred) return preferred;

  // Fallback: largest vertical scroller in the ancestor chain (page-sized)
  let best = null;
  let bestH = 0;
  for (let el = node?.parentElement; el; el = el.parentElement) {
    const { overflowY } = getComputedStyle(el);
    if (overflowY !== 'auto' && overflowY !== 'scroll') continue;
    if (el.clientHeight > bestH) {
      bestH = el.clientHeight;
      best = el;
    }
  }
  return best || document.documentElement;
}

function thumbMetrics(trackWidth, scrollWidth, clientWidth, scrollLeft) {
  const maxScroll = Math.max(0, scrollWidth - clientWidth);
  if (trackWidth <= 0 || scrollWidth <= 0 || maxScroll <= 0) {
    return { thumbWidth: trackWidth, thumbLeft: 0, maxScroll: 0 };
  }
  const thumbWidth = Math.max(MIN_THUMB, (clientWidth / scrollWidth) * trackWidth);
  const travel = Math.max(0, trackWidth - thumbWidth);
  const thumbLeft = travel > 0 ? (scrollLeft / maxScroll) * travel : 0;
  return { thumbWidth, thumbLeft, maxScroll };
}

/**
 * @param {boolean} enabled
 * @param {unknown} [contentKey] — change when inner content width may change
 *   without the host resizing (e.g. code string, table column count)
 */
export function useStickyHorizontalScroll(enabled = true, contentKey = 0) {
  const scrollRef = useRef(null);
  const trackRef = useRef(null);
  const dragRef = useRef(null);
  const [layout, setLayout] = useState({
    show: false,
    left: 0,
    width: 0,
    scrollWidth: 0,
    clientWidth: 0,
    scrollLeft: 0,
  });

  useLayoutEffect(() => {
    if (!enabled) {
      const el = scrollRef.current;
      el?.classList.remove('doc-sticky-hscroll-host--active');
      setLayout((s) => (s.show ? { ...s, show: false } : s));
      return undefined;
    }

    const scrollEl = scrollRef.current;
    if (!scrollEl) return undefined;

    const scroller = findPageScroller(scrollEl);

    const update = (portRect) => {
      const el = scrollRef.current;
      if (!el) return;

      const overflowsX = el.scrollWidth > el.clientWidth + 1;
      const elRect = el.getBoundingClientRect();
      const port = portRect || portRectOf(scroller);

      const intersects = elRect.top < port.bottom && elRect.bottom > port.top;
      const bottomClipped = elRect.bottom > port.bottom - 1;
      const show = overflowsX && intersects && bottomClipped;

      // left/width track the host; vertical position is pure CSS
      // (bottom: var(--doc-sticky-hscroll-lift) on the window).
      setLayout((prev) => {
        const next = {
          show,
          left: elRect.left,
          width: elRect.width,
          scrollWidth: el.scrollWidth,
          clientWidth: el.clientWidth,
          scrollLeft: el.scrollLeft,
        };
        if (
          prev.show === next.show &&
          prev.left === next.left &&
          prev.width === next.width &&
          prev.scrollWidth === next.scrollWidth &&
          prev.clientWidth === next.clientWidth &&
          prev.scrollLeft === next.scrollLeft
        ) {
          return prev;
        }
        return next;
      });

      el.classList.toggle('doc-sticky-hscroll-host--active', show);
    };

    const group = ensureGroup(scroller);
    const host = {
      group,
      visible: true,
      measure: (portRect) => update(portRect),
      hide: () => {
        const el = scrollRef.current;
        el?.classList.remove('doc-sticky-hscroll-host--active');
        setLayout((prev) => (prev.show ? { ...prev, show: false } : prev));
      },
    };
    hostByEl.set(scrollEl, host);
    group.hosts.add(host);
    group.io?.observe(scrollEl);

    const onHostScroll = () => {
      if (!host.visible) return;
      scheduleGroup(group);
    };
    scrollEl.addEventListener('scroll', onHostScroll, { passive: true });

    const ro = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => {
        if (host.visible) scheduleGroup(group);
      })
      : null;
    ro?.observe(scrollEl);
    Array.from(scrollEl.children).forEach((child) => ro?.observe(child));
    if (scroller !== document.documentElement && scroller !== document.body) {
      ro?.observe(scroller);
    }
    scheduleGroup(group);

    return () => {
      scrollEl.removeEventListener('scroll', onHostScroll);
      scrollEl.classList.remove('doc-sticky-hscroll-host--active');
      ro?.disconnect();
      group.io?.unobserve(scrollEl);
      group.hosts.delete(host);
      hostByEl.delete(scrollEl);
      releaseGroup(group);
    };
  }, [enabled, contentKey]);

  const { thumbWidth, thumbLeft, maxScroll } = thumbMetrics(
    layout.width,
    layout.scrollWidth,
    layout.clientWidth,
    layout.scrollLeft
  );

  const setHostScrollLeft = (next) => {
    const el = scrollRef.current;
    if (!el || maxScroll <= 0) return;
    el.scrollLeft = Math.max(0, Math.min(maxScroll, next));
  };

  const onThumbPointerDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const el = scrollRef.current;
    if (!el || maxScroll <= 0) return;

    const travel = Math.max(1, layout.width - thumbWidth);
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startScroll: el.scrollLeft,
      travel,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onThumbPointerMove = (e) => {
    const drag = dragRef.current;
    if (!drag || e.pointerId !== drag.pointerId) return;
    const dx = e.clientX - drag.startX;
    setHostScrollLeft(drag.startScroll + (dx / drag.travel) * maxScroll);
  };

  const onThumbPointerUp = (e) => {
    const drag = dragRef.current;
    if (!drag || e.pointerId !== drag.pointerId) return;
    dragRef.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* already released */
    }
  };

  const onTrackPointerDown = (e) => {
    if (e.target !== trackRef.current) return;
    e.preventDefault();
    const travel = Math.max(1, layout.width - thumbWidth);
    const x = e.clientX - layout.left - thumbWidth / 2;
    setHostScrollLeft((x / travel) * maxScroll);
  };

  const bar =
    enabled && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={trackRef}
            className={`doc-sticky-hscroll${layout.show ? ' is-visible' : ''}`}
            style={{
              left: layout.left,
              width: layout.width,
            }}
            aria-hidden={!layout.show}
            onPointerDown={onTrackPointerDown}
          >
            <div
              className="doc-sticky-hscroll__thumb"
              style={{
                width: thumbWidth,
                transform: `translateX(${thumbLeft}px)`,
              }}
              onPointerDown={onThumbPointerDown}
              onPointerMove={onThumbPointerMove}
              onPointerUp={onThumbPointerUp}
              onPointerCancel={onThumbPointerUp}
            />
          </div>,
          document.body
        )
      : null;

  return { scrollRef, bar };
}
